import { frameId } from "../assets/manifest.ts";
import type { PropPlayback } from "./catalogue.ts";

/**
 * The index of every pack's palette props (ADR-129 decision 1.4): what the Props palette searches beyond the
 * packs a world lists. It is one for every world and says nothing about any of them; which entries a world
 * already offers is worked out against that world's own catalogue. A host builds it from the catalogues of its
 * pack table and the word tables it holds, and nothing here names a pack, a word or a category.
 */

/**
 * One prop frame of a pack's catalogue, as much of it as the index reads: its local tile id, its display name
 * and group, its image's path in the pack, and its place in a strip when it is one of a strip's frames (a strip
 * carries the path the palette places it by).
 */
export interface PackIndexFrame {
  readonly tile: number;
  readonly name: string;
  readonly group: string;
  readonly path?: string;
  readonly strip?: { readonly frames: readonly number[]; readonly index: number; readonly plays: "loop" | "once"; readonly path?: string };
}

/**
 * One tileset of a pack's catalogue, as much of it as the index reads: its id, how the editor offers it, the
 * size of each tile that has its own image, and its prop frames. Only a `props` tileset yields entries.
 */
export interface PackIndexTileset {
  readonly id: string;
  readonly role: "terrain" | "props";
  readonly tileset: { readonly tileWidth: number; readonly tileHeight: number; readonly tiles: readonly { readonly id: number; readonly image: { readonly width: number; readonly height: number } | null }[] };
  readonly frames: readonly PackIndexFrame[];
}

/**
 * One pack's catalogue, as much of it as the index reads. The catalogue an importer yields for a pack has this
 * shape and is passed as it is.
 */
export interface PackIndexCatalogue {
  readonly pack: string;
  readonly tilesets: readonly PackIndexTileset[];
}

/**
 * One pack's search words, as the host's reader of its word tables gives them: by frame id, as
 * `frameId(tileset, null, tile)` writes it, the words and the category of a prop frame, and beside them the
 * words that say where the whole pack is set. A frame the table does not name has neither words nor a
 * category, and a blank category is none. Any other field of a table, its schema number among them, is not
 * read.
 */
export interface PackWordTable {
  readonly setting?: readonly string[];
  readonly frames: { readonly [frame: string]: { readonly words?: readonly string[]; readonly category?: string } };
}

/**
 * The words that say where one pack is set. No entry of the index carries them, since a word every prop of a
 * pack carries tells none of them apart: a search reads them once for the pack, beside its entries.
 */
export interface PackSetting {
  readonly pack: string;
  readonly setting: readonly string[];
}

/**
 * One thing a palette can place from a pack: a still, or a strip once, by its first frame. `id` is the frame's
 * id, `size` its width and height in pixels, `frames` how many frames it plays (1 for a still), `plays` how,
 * `words` and `category` what a search matches beside the name and the group, `path` the prop's image path in its
 * pack when the host knows it (the folder the Folder filter names; an entry without one is filed under its
 * tileset), `cell` the entry's place on
 * its pack's thumbnail sheet, counted from 0 within the pack, `preview`, on a strip a host has written the
 * frames of, where they are and how long each shows, and `with`, the frames a host's rule says the prop's
 * states and transitions rest on (ADR-129 decision 3.9), which a pull takes with it.
 */
export interface PackIndexEntry {
  readonly id: string;
  readonly pack: string;
  readonly tileset: string;
  readonly tile: number;
  readonly name: string;
  readonly group: string;
  readonly words: readonly string[];
  readonly category?: string;
  readonly path?: string;
  readonly size: readonly [width: number, height: number];
  readonly frames: number;
  readonly plays: PropPlayback;
  readonly cell: number;
  readonly preview?: PackIndexPreview;
  readonly with?: readonly PackIndexWithFrame[];
}

/**
 * One frame an entry brings with it: where it stands in its pack, its name, its size in pixels and how many
 * frames it plays (1 for a still), which is all a confirm needs to count and cost it. A strip is named by its
 * first frame, as the document names it.
 */
export interface PackIndexWithFrame {
  readonly tileset: string;
  readonly tile: number;
  readonly name: string;
  readonly size: readonly [width: number, height: number];
  readonly frames: number;
}

/**
 * The host's rule for what a prop brings (ADR-129 decision 3.9): given the name of a placed prop frame and the
 * names of the prop frames of its tileset, the names of the frames the host's object types would give its states
 * and its transitions. The index finds each name in the pack, the prop's own tileset first, as the editor finds
 * a state frame by name. The rule is the host's and names no game thing here.
 */
export type PackIndexWithRule = (placed: string, names: readonly string[]) => readonly string[];

/**
 * The most frames one entry brings with it, so an entry's pull stays far inside the caps of one operation.
 */
export const PACK_INDEX_WITH_MOST = 16;

/**
 * A strip's frames for a preview before any pull (ADR-129 decision 2.3a): `file` names, beside the index, an
 * image of every frame at its own size, side by side in play order, and `durations` is each frame's time on
 * screen in milliseconds.
 */
export interface PackIndexPreview {
  readonly file: string;
  readonly durations: readonly number[];
}

/**
 * Builds the index: one entry for each prop frame of each pack's `props` tilesets that is a still or the first
 * frame of a strip, the packs in the order given, each pack's tilesets and frames in the catalogue's order. A
 * strip's later frames, tile cells and terrains yield nothing. When the host passes a rule, an entry whose
 * frame the rule names others for lists them as `with`: found in the same pack by name, the entry's own
 * tileset first, at most `PACK_INDEX_WITH_MOST` of them, never the entry's own frame.
 *
 * @example
 * const index = packIndex(catalogues, { highlands: { frames: { "highlands:Props/default/3": { words: ["campfire"] } } } });
 *
 * @param catalogues The packs' catalogues, in the host's pack table order.
 * @param wordTables Each pack's word table, by pack id; a pack without one has no words.
 * @param withRule The host's rule for what a prop brings; without one no entry has `with`.
 * @returns The entries, plain data that shares nothing with the inputs.
 * @throws Error when a tileset's id is empty or holds a slash, which no frame id can carry.
 */
export function packIndex(catalogues: readonly PackIndexCatalogue[], wordTables: { readonly [pack: string]: PackWordTable } = {}, withRule?: PackIndexWithRule): PackIndexEntry[] {
  const entries: PackIndexEntry[] = [];
  for (const catalogue of catalogues) {
    const worded = Object.hasOwn(wordTables, catalogue.pack) ? (wordTables[catalogue.pack] as PackWordTable).frames : {};
    const heads = withRule === undefined ? null : headsOf(catalogue);
    let cell = 0;
    for (const tileset of catalogue.tilesets) {
      if (tileset.role !== "props") {
        continue;
      }
      const names = heads?.names.get(tileset.id) ?? [];
      for (const frame of tileset.frames) {
        if (frame.strip !== undefined && frame.strip.index !== 0) {
          continue;
        }
        const id = frameId(tileset.id, null, frame.tile);
        const path = pathOfFrame(frame);
        const said = Object.hasOwn(worded, id) ? worded[id] : undefined;
        const brought = withRule === undefined || heads === null ? [] : broughtWith(heads, tileset.id, frame, withRule(frame.name, names));
        entries.push({
          id,
          pack: catalogue.pack,
          tileset: tileset.id,
          tile: frame.tile,
          name: frame.name,
          group: frame.group,
          words: [...(said?.words ?? [])],
          ...(said?.category === undefined || said.category === "" ? {} : { category: said.category }),
          ...(path === undefined ? {} : { path }),
          size: sizeOfTile(tileset, frame.tile),
          frames: frame.strip?.frames.length ?? 1,
          plays: frame.strip?.plays ?? "static",
          cell,
          ...(brought.length === 0 ? {} : { with: brought }),
        });
        cell += 1;
      }
    }
  }
  return entries;
}

/**
 * The path a frame is placed by: its strip's, else its own.
 *
 * @param frame The frame.
 * @returns The path, or undefined when the catalogue gave none or an empty one.
 */
function pathOfFrame(frame: PackIndexFrame): string | undefined {
  const path = frame.strip?.path ?? frame.path;
  return path === undefined || path === "" ? undefined : path;
}

/**
 * The size of a tile's picture: its own image's, else the tileset's tile size.
 *
 * @param tileset The tileset.
 * @param tile The local tile id.
 * @returns Width and height in pixels.
 */
function sizeOfTile(tileset: PackIndexTileset, tile: number): readonly [number, number] {
  const image = tileset.tileset.tiles.find((each) => each.id === tile)?.image ?? null;
  return image === null ? [tileset.tileset.tileWidth, tileset.tileset.tileHeight] : [image.width, image.height];
}

/**
 * One pack's prop frames that a name can find: each frame that is a still or a strip's first, by name in the
 * catalogue's order, and each props tileset's names in order.
 */
interface PackHeads {
  readonly byName: ReadonlyMap<string, readonly PackIndexWithFrame[]>;
  readonly names: ReadonlyMap<string, readonly string[]>;
}

/**
 * Reads a pack's findable frames once, for every entry's rule.
 *
 * @param catalogue The pack's catalogue.
 * @returns The frames by name, and the names of each props tileset.
 */
function headsOf(catalogue: PackIndexCatalogue): PackHeads {
  const byName = new Map<string, PackIndexWithFrame[]>();
  const names = new Map<string, string[]>();
  for (const tileset of catalogue.tilesets) {
    if (tileset.role !== "props") {
      continue;
    }
    const own: string[] = [];
    for (const frame of tileset.frames) {
      if (frame.strip !== undefined && frame.strip.index !== 0) {
        continue;
      }
      own.push(frame.name);
      const listed = byName.get(frame.name) ?? [];
      listed.push({ tileset: tileset.id, tile: frame.tile, name: frame.name, size: sizeOfTile(tileset, frame.tile), frames: frame.strip?.frames.length ?? 1 });
      byName.set(frame.name, listed);
    }
    names.set(tileset.id, own);
  }
  return { byName, names };
}

/**
 * The frames a rule's names find for one entry: each name's frame in the entry's own tileset, else the first
 * elsewhere in the pack, left out when it is the entry's own frame or was found already, cut at
 * `PACK_INDEX_WITH_MOST`.
 *
 * @param heads The pack's findable frames.
 * @param tileset The entry's tileset.
 * @param frame The entry's frame.
 * @param wanted The names the host's rule gave.
 * @returns The frames, in the order named.
 */
function broughtWith(heads: PackHeads, tileset: string, frame: PackIndexFrame, wanted: readonly string[]): PackIndexWithFrame[] {
  const brought: PackIndexWithFrame[] = [];
  for (const name of wanted) {
    const found = heads.byName.get(name);
    const chosen = found?.find((each) => each.tileset === tileset) ?? found?.[0];
    if (chosen === undefined || (chosen.tileset === tileset && chosen.tile === frame.tile) || brought.some((each) => each.tileset === chosen.tileset && each.tile === chosen.tile)) {
      continue;
    }
    brought.push({ tileset: chosen.tileset, tile: chosen.tile, name: chosen.name, size: [chosen.size[0], chosen.size[1]], frames: chosen.frames });
    if (brought.length === PACK_INDEX_WITH_MOST) {
      break;
    }
  }
  return brought;
}

/**
 * What a pull of one entry takes: the entry's frame and the frames it brings, by tileset, each list ascending
 * and distinct, the entry's own tileset first and the others in the order the entry names them.
 *
 * @param entry The entry.
 * @returns One `{ tileset, tiles }` for each tileset the pull touches.
 */
export function entryPicks(entry: Pick<PackIndexEntry, "tileset" | "tile" | "with">): { readonly tileset: string; readonly tiles: readonly number[] }[] {
  const by = new Map<string, Set<number>>([[entry.tileset, new Set([entry.tile])]]);
  for (const brought of entry.with ?? []) {
    by.set(brought.tileset, (by.get(brought.tileset) ?? new Set<number>()).add(brought.tile));
  }
  return [...by].map(([tileset, tiles]) => ({ tileset, tiles: [...tiles].sort((a, b) => a - b) }));
}

/**
 * Each pack's setting words, for the index to hold once a pack beside its entries.
 *
 * @param catalogues The packs' catalogues, in the host's pack table order.
 * @param wordTables Each pack's word table, by pack id.
 * @returns One line a pack of the catalogues, in their order; a pack without a table, or whose table names no
 *   setting, has none.
 */
export function packSettings(catalogues: readonly { readonly pack: string }[], wordTables: { readonly [pack: string]: PackWordTable } = {}): PackSetting[] {
  return catalogues.map((catalogue) => ({ pack: catalogue.pack, setting: [...((Object.hasOwn(wordTables, catalogue.pack) ? wordTables[catalogue.pack]?.setting : undefined) ?? [])] }));
}
